import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { scrollIntoView } from '@theme/scrolling';

/**
 * @typedef {Object} LpsStickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} scrollButton - Sticky bar's button
 */

/**
 * A custom element that manages the LPS sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view. Unlike the theme's
 * default `sticky-add-to-cart`, this one never shows a variant/size picker
 * and never adds to cart directly - its button always scrolls back up to
 * the buy box, where the real variant picker and add-to-cart button live.
 *
 * @extends {Component<LpsStickyAddToCartRefs>}
 */
class LpsStickyAddToCart extends Component {
  requiredRefs = ['stickyBar', 'scrollButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {number | undefined} */
  #highlightTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#dismissCallout, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    clearTimeout(this.#highlightTimeout);
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const buyButtonsBlock = this.#getBuyButtonsBlock();
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
  }

  // Public action handlers
  /**
   * Handles the sticky bar button click - scrolls back up to the buy box
   * (variant picker + real add-to-cart button) instead of adding to cart,
   * and shows a speech-bubble callout on the variant picker so it's obvious
   * a size still needs to be picked there.
   */
  handleScrollToBuyBox = () => {
    const buyButtonsBlock = this.#getBuyButtonsBlock();
    if (!buyButtonsBlock) return;
    scrollIntoView(buyButtonsBlock, { block: 'center' });
    this.#showCallout();
  };

  /**
   * Shows a speech-bubble callout above the variant picker, auto-dismissed
   * after a few seconds or as soon as the shopper picks a variant.
   */
  #showCallout() {
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return;

    const callout = this.#getOrCreateCallout(variantPicker);

    clearTimeout(this.#highlightTimeout);
    callout.classList.remove('variant-picker-callout--visible');
    // Force a reflow so the transition restarts if it's still running from a previous click
    void callout.offsetWidth;
    callout.classList.add('variant-picker-callout--visible');

    this.#highlightTimeout = setTimeout(this.#dismissCallout, 3200);
  }

  /**
   * Hides the callout, if one is currently showing.
   */
  #dismissCallout = () => {
    clearTimeout(this.#highlightTimeout);
    this.#getVariantPicker()
      ?.querySelector('.variant-picker-callout')
      ?.classList.remove('variant-picker-callout--visible');
  };

  /**
   * Gets the existing callout element for a variant picker, creating it on first use.
   * @param {Element} variantPicker
   * @returns {HTMLElement}
   */
  #getOrCreateCallout(variantPicker) {
    let callout = /** @type {HTMLElement | null} */ (variantPicker.querySelector('.variant-picker-callout'));
    if (!callout) {
      callout = document.createElement('div');
      callout.className = 'variant-picker-callout';
      callout.setAttribute('role', 'status');
      callout.textContent = this.dataset.selectSizeText || '';
      variantPicker.prepend(callout);
    }
    return callout;
  }

  /**
   * Handles variant update events - morphs price/image/availability so the
   * bar stays in sync with whatever variant is selected in the buy box.
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const newStickyAddToCart = event.detail.data.html.querySelector('lps-sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    const variant = event.detail.resource;
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Gets the buy buttons block for the product on this page
   * @returns {HTMLElement | null}
   */
  #getBuyButtonsBlock() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    const productForm = document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
    return productForm?.closest('.buy-buttons-block') ?? null;
  }

  /**
   * Gets the variant picker for the product on this page
   * @returns {HTMLElement | null}
   */
  #getVariantPicker() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    return sectionElement?.querySelector(`variant-picker[data-product-id="${productId}"]`) ?? null;
  }
}

if (!customElements.get('lps-sticky-add-to-cart')) {
  customElements.define('lps-sticky-add-to-cart', LpsStickyAddToCart);
}
